import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Availability, Campsite } from '../types';

type SortMode = 'name' | 'availability';

function addUtcDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function toIsoDateOnly(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function formatWeekday(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00.000Z`);
  return d.toLocaleDateString('en-US', { weekday: 'short' });
}

function enumerateIsoDatesInclusive(startIso: string, endIso: string): string[] {
  const start = new Date(`${startIso}T00:00:00.000Z`);
  const end = new Date(`${endIso}T00:00:00.000Z`);
  const out: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    out.push(toIsoDateOnly(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

export function FacilityAvailabilityGrid(props: {
  campsites: Campsite[];
  availability: Availability[];
  startDate: string;
  endDate: string;
  highlightStartDate?: string;
  highlightEndDate?: string;
  selectedCampsiteId: string | null;
  onSelectCampsite: (campsite: Campsite) => void;
  onRangeChange?: (range: { startDate: string; endDate: string }) => void;
  firstComeFirstServed?: boolean;
}) {
  const INITIAL_ROW_LIMIT = 25;
  const [sortMode, setSortMode] = useState<SortMode>('availability');
  const [showAll, setShowAll] = useState(false);

  const dates = useMemo(
    () => enumerateIsoDatesInclusive(props.startDate, props.endDate),
    [props.endDate, props.startDate]
  );

  const highlightedDates = useMemo(() => {
    if (!props.highlightStartDate || !props.highlightEndDate) return new Set<string>();
    return new Set(enumerateIsoDatesInclusive(props.highlightStartDate, props.highlightEndDate));
  }, [props.highlightStartDate, props.highlightEndDate]);

  const availabilityByKey = useMemo(() => {
    const map = new Set<string>();
    for (const row of props.availability) {
      map.add(`${row.campsiteId}:${row.date}`);
    }
    return map;
  }, [props.availability]);

  const availabilityCountBySite = useMemo(() => {
    const counts = new Map<string, number>();
    for (const campsite of props.campsites) {
      let count = 0;
      for (const date of dates) {
        if (availabilityByKey.has(`${campsite.id}:${date}`)) {
          count++;
        }
      }
      counts.set(campsite.id, count);
    }
    return counts;
  }, [availabilityByKey, dates, props.campsites]);

  const sortedCampsites = useMemo(() => {
    const rows = [...props.campsites];
    if (sortMode === 'name') {
      rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
      return rows;
    }
    rows.sort((a, b) => {
      const aCount = availabilityCountBySite.get(a.id) ?? 0;
      const bCount = availabilityCountBySite.get(b.id) ?? 0;
      if (aCount !== bCount) return bCount - aCount;
      return a.name.localeCompare(b.name, undefined, { numeric: true });
    });
    return rows;
  }, [availabilityCountBySite, props.campsites, sortMode]);

  const visibleCampsites = showAll
    ? sortedCampsites
    : sortedCampsites.slice(0, INITIAL_ROW_LIMIT);
  const hasMore = sortedCampsites.length > INITIAL_ROW_LIMIT;

  const headerLabel = useMemo(() => {
    const start = new Date(`${props.startDate}T00:00:00.000Z`);
    return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [props.startDate]);

  const handlePrev = () => {
    const start = new Date(`${props.startDate}T00:00:00.000Z`);
    const nextStart = addUtcDays(start, -7);
    const nextEnd = addUtcDays(nextStart, dates.length - 1);
    props.onRangeChange?.({
      startDate: toIsoDateOnly(nextStart),
      endDate: toIsoDateOnly(nextEnd),
    });
  };

  const handleNext = () => {
    const start = new Date(`${props.startDate}T00:00:00.000Z`);
    const nextStart = addUtcDays(start, 7);
    const nextEnd = addUtcDays(nextStart, dates.length - 1);
    props.onRangeChange?.({
      startDate: toIsoDateOnly(nextStart),
      endDate: toIsoDateOnly(nextEnd),
    });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {props.firstComeFirstServed && (
        <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          First-come, first-served — sites at this campground can't be reserved
          in advance. "FF" marks walk-up-only sites.
        </div>
      )}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrev}
            className="p-2 rounded-lg hover:bg-gray-50 transition-colors"
            aria-label="Previous week"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-sm font-semibold text-gray-900">{headerLabel}</div>
          <button
            type="button"
            onClick={handleNext}
            className="p-2 rounded-lg hover:bg-gray-50 transition-colors"
            aria-label="Next week"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Sort by</label>
          <select
            className="text-sm border border-gray-200 rounded-lg px-2 py-1 bg-white"
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value as SortMode)}
          >
            <option value="availability">Availability</option>
            <option value="name">Name</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 z-10 bg-gray-50 text-left font-semibold text-gray-700 px-4 py-2 min-w-[220px]">
                Site List
              </th>
              {dates.map((date) => {
                const isHighlighted = highlightedDates.has(date);
                return (
                  <th
                    key={date}
                    className={`text-center font-medium px-2 py-2 min-w-[56px] ${
                      isHighlighted
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'text-gray-600'
                    }`}
                    title={date}
                  >
                    <div className="leading-4">{formatWeekday(date)}</div>
                    <div className={`font-semibold ${isHighlighted ? 'text-emerald-800' : 'text-gray-900'}`}>
                      {Number(date.slice(8, 10))}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visibleCampsites.map((campsite) => {
              const selected = props.selectedCampsiteId === campsite.id;
              const rowClass = selected ? 'bg-emerald-50' : 'bg-white';
              return (
                <tr key={campsite.id} className={`${rowClass} border-t border-gray-100`}>
                  <td
                    className={`sticky left-0 z-10 px-4 py-2 min-w-[220px] ${
                      selected ? 'bg-emerald-50' : 'bg-white'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => props.onSelectCampsite(campsite)}
                      className="w-full text-left"
                    >
                      <div className="font-medium text-gray-900">{campsite.name}</div>
                      <div className="text-xs text-gray-600">
                        {availabilityCountBySite.get(campsite.id) ?? 0} available
                      </div>
                    </button>
                  </td>
                  {dates.map((date) => {
                    const isAvailable = availabilityByKey.has(`${campsite.id}:${date}`);
                    const isHighlighted = highlightedDates.has(date);
                    return (
                      <td key={date} className={`px-2 py-2 text-center ${isHighlighted ? 'bg-emerald-50/50' : ''}`}>
                        {props.firstComeFirstServed ? (
                          <span
                            className="mx-auto flex h-8 w-10 items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-[11px] font-medium text-gray-500"
                            title={`${date}: First-come, first-served`}
                          >
                            FF
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => props.onSelectCampsite(campsite)}
                            className={`mx-auto h-8 w-10 rounded-md border transition-colors ${
                              isAvailable
                                ? 'bg-emerald-600 border-emerald-600 hover:bg-emerald-700'
                                : 'bg-gray-100 border-gray-200 hover:bg-gray-200'
                            }`}
                            aria-label={`${campsite.name} ${date} ${isAvailable ? 'available' : 'unavailable'}`}
                            title={`${date}: ${isAvailable ? 'Available' : 'Unavailable'}`}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasMore && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="w-full px-4 py-3 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50/50 border-t border-gray-100 transition-colors"
        >
          Show all {sortedCampsites.length} sites
        </button>
      )}

      <div className="flex items-center gap-4 px-4 py-3 border-t border-gray-100 text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded bg-emerald-600" />
          <span>Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded bg-gray-100 border border-gray-200" />
          <span>Unavailable</span>
        </div>
      </div>
    </div>
  );
}

